import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router"
import api from "@/lib/api"
import ModuleLayout from "@/components/admin/ModuleLayout"
import Badge from "@/components/ui/badge/Badge"
import { getErrorMessage } from "@/lib/errors"
import { LoadState } from "@/components/ui/LoadState"

type MyGroup = {
  id: string
  name: string
  groupType: string
  seedbed: { id: string; name: string } | null
  studentCount: number
}

type StudentRow = { id: string; name: string; email: string; status: "active" | "suspended" | "ended"; assignedAt: string }
type ProgressRow = { userId: string; name: string; progressPercent: number }

const STATUS_LABELS: Record<string, string> = { active: "Activo", suspended: "Suspendido", ended: "Finalizado" }

function RetryAlert({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm text-error-600 dark:text-error-400">
      <p className="min-w-0">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="min-h-11 rounded-lg border border-gray-300 px-4 font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
      >
        Reintentar
      </button>
    </div>
  )
}

export default function MyGroupPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const groupsQuery = useQuery({
    queryKey: ["my-group", "list"],
    queryFn: async () => (await api.get<{ data: { groups: MyGroup[] } }>("/academy/seedbeds/my-group")).data.data.groups,
  })

  const groups = groupsQuery.data
  const requestedGroupId = searchParams.get("group")
  const selectedGroupId = groups?.find((g) => g.id === requestedGroupId)?.id ?? groups?.[0]?.id ?? null

  const studentsQuery = useQuery({
    queryKey: ["my-group", "students", selectedGroupId],
    queryFn: async () => (await api.get<{ data: { students: StudentRow[] } }>("/academy/seedbeds/my-group/students", { params: { groupId: selectedGroupId } })).data.data.students,
    enabled: !!selectedGroupId,
  })

  const progressQuery = useQuery({
    queryKey: ["my-group", "progress", selectedGroupId],
    queryFn: async () => (await api.get<{ data: { progress: ProgressRow[] } }>("/academy/seedbeds/my-group/progress", { params: { groupId: selectedGroupId } })).data.data.progress,
    enabled: !!selectedGroupId,
  })

  const progressByUser = new Map((progressQuery.data ?? []).map((p) => [p.userId, p.progressPercent]))
  const progressReady = progressQuery.isSuccess
  const sortedStudents = [...(studentsQuery.data ?? [])]
  if (progressReady) sortedStudents.sort((a, b) => (progressByUser.get(a.id) ?? 0) - (progressByUser.get(b.id) ?? 0))

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Docencia", "Mi grupo"]}
      title="Mi grupo"
      subtitle="Seguimiento de los semilleros/grupos donde eres instructor asignado"
      tabs={[]}
      metrics={[]}
    >
      {groupsQuery.isLoading && <LoadState />}
      {groupsQuery.isError && <RetryAlert message={getErrorMessage(groupsQuery.error)} onRetry={() => groupsQuery.refetch()} />}

      {groupsQuery.data && groupsQuery.data.length === 0 && (
        <p className="text-sm text-gray-500">No tienes ningún grupo/semillero asignado como instructor todavía.</p>
      )}

      {groupsQuery.data && groupsQuery.data.length > 0 && (
        <div className="space-y-6">
          <div role="group" aria-label="Grupos" className="flex flex-wrap gap-2">
            {groupsQuery.data.map((g) => (
              <button
                key={g.id}
                type="button"
                aria-pressed={selectedGroupId === g.id}
                onClick={() => setSearchParams({ group: g.id }, { replace: true })}
                className={`min-h-11 max-w-full rounded-lg border px-3 text-left text-sm font-medium ${
                  selectedGroupId === g.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
                }`}
              >
                {g.name} {g.seedbed ? `· ${g.seedbed.name}` : ""} ({g.studentCount})
              </button>
            ))}
          </div>

          {selectedGroupId && (
            <section aria-label="Estudiantes del grupo" className="rounded-xl border border-gray-200 dark:border-gray-800">
              {studentsQuery.isLoading && <LoadState />}
              {studentsQuery.isError && <RetryAlert message={getErrorMessage(studentsQuery.error)} onRetry={() => studentsQuery.refetch()} />}
              {progressQuery.isError && (
                <RetryAlert
                  message={`No se pudo cargar el avance. ${getErrorMessage(progressQuery.error)}`}
                  onRetry={() => progressQuery.refetch()}
                />
              )}
              {progressReady && sortedStudents.length > 1 && (
                <p className="px-3 pt-3 text-xs text-gray-500 dark:text-gray-400">Ordenado por menor avance</p>
              )}
              {sortedStudents.length === 0 && studentsQuery.isSuccess && (
                <p className="p-4 text-sm text-gray-500">Sin estudiantes inscritos en este grupo todavía.</p>
              )}
              {sortedStudents.length > 0 && (
                <ul aria-label="Estudiantes" className="divide-y divide-gray-100 dark:divide-gray-800">
                  {sortedStudents.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-900 dark:text-white">{s.name}</p>
                        <p className="truncate text-gray-500 dark:text-gray-400">{s.email}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="tabular-nums text-gray-500 dark:text-gray-400">
                          {progressReady ? `${progressByUser.get(s.id) ?? 0}% de avance` : <span aria-label="Avance no disponible">—</span>}
                        </span>
                        <Badge color={s.status === "active" ? "success" : s.status === "suspended" ? "warning" : "light"} size="sm">
                          {STATUS_LABELS[s.status] ?? s.status}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      )}
    </ModuleLayout>
  )
}
